import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';

import { User } from '@userModule/models/user.model';
import { RoleMenuPermission } from '@auth/models/role-menu-permission.model';

/**
 * Type definition for the user state.
 */
type UserState = {
  user: User | null;
  rolePermissions: RoleMenuPermission[];
};

/**
 * The initial state of the user store.
 */
const initialState: UserState = {
  user: null,
  rolePermissions: [],
};

/**
 * Store for managing user-related state.
 */
export const UserStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store) => ({
    /**
     * Sets the current user.
     * @param user The user to set.
     */
    setUser(user: User | null): void {
      patchState(store, { user });
    },
    /**
     * Sets the role permissions for the current user.
     * @param rolePermissions The role permissions to set.
     */
    setRolePermissions(rolePermissions: RoleMenuPermission[]): void {
      patchState(store, { rolePermissions });
    },
    /**
     * Clears the current user and role permissions.
     */
    clearUser(): void {
      patchState(store, { user: null, rolePermissions: [] });
    },
    /**
     * Adds a role permission to the store.
     * @param rolePermission The role permission to add.
     */
    addRolePermission(rolePermission: RoleMenuPermission): void {
      patchState(store, ({ rolePermissions }) => ({
        rolePermissions: [...rolePermissions, rolePermission]
      }));
    },
    /**
     * Removes a role permission from the store.
     * @param rolePermissionId The ID of the role permission to remove.
     */
    removeRolePermission(rolePermissionId: number): void {
      patchState(store, ({ rolePermissions }) => ({
        rolePermissions: rolePermissions.filter((rp) => rp.id !== rolePermissionId)
      }));
    }
  }))
);
